import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ShoppingBag } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const EMPTY = { name: "", units: "", price: "", uses: "", image: "" };

export default function OTCEditor() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // product or "new"
  const [form, setForm] = useState(EMPTY);
  const [delTarget, setDelTarget] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => { load(); }, []);
  async function load() { try { const { data } = await api.get("/otc/products"); setProducts(data); } catch { /* */ } }

  function openNew() { setForm(EMPTY); setEditing("new"); }
  function openEdit(p) { setForm({ name: p.name, units: p.units, price: p.price, uses: p.uses, image: p.image || "" }); setEditing(p); }

  async function save(e) {
    e.preventDefault(); setLoading(true);
    const payload = { ...form, price: Number(form.price) };
    try {
      if (editing === "new") await api.post("/otc/products", payload);
      else await api.put(`/otc/products/${editing.id}`, payload);
      toast.success("Product saved."); setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setLoading(false); }
  }

  async function confirmDelete() {
    try { await api.delete(`/otc/products/${delTarget.id}`); toast.success("Product deleted."); setDelTarget(null); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">OTC Store Editor</h1>
          <p className="mt-1 text-sm text-muted-foreground">Add and edit over-the-counter products shown to patients.</p>
        </div>
        <Button onClick={openNew} className="rounded-full" data-testid="otc-add-product"><Plus className="mr-1 h-4 w-4" /> Add Product</Button>
      </div>

      {products.length === 0 ? (
        <div className="mt-8 rounded-xl border border-dashed border-border p-16 text-center text-muted-foreground">
          <ShoppingBag className="mx-auto mb-3 h-10 w-10 opacity-40" /> No products yet. Add your first product.
        </div>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p) => (
            <div key={p.id} className="rounded-xl border border-border/60 bg-card p-5" data-testid={`otc-edit-card-${p.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-heading font-bold">{p.name}</h3>
                  <p className="text-xs uppercase text-muted-foreground">{p.units}</p>
                </div>
                <span className="font-heading font-bold text-primary">{money(p.price)}</span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{p.uses}</p>
              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => openEdit(p)} data-testid={`otc-edit-${p.id}`}><Pencil className="mr-1 h-3 w-3" /> Edit</Button>
                <Button size="sm" variant="ghost" className="rounded-full text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setDelTarget(p)} data-testid={`otc-delete-${p.id}`}><Trash2 className="mr-1 h-3 w-3" /> Delete</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing === "new" ? "Add Product" : "Edit Product"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2"><Label>Product name</Label><Input data-testid="otc-form-name" value={form.name} onChange={set("name")} required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Units</Label><Input data-testid="otc-form-units" placeholder="e.g. 24 tablets" value={form.units} onChange={set("units")} required /></div>
              <div className="space-y-2"><Label>Price ($)</Label><Input type="number" step="0.01" data-testid="otc-form-price" value={form.price} onChange={set("price")} required /></div>
            </div>
            <div className="space-y-2"><Label>Uses</Label><Textarea data-testid="otc-form-uses" value={form.uses} onChange={set("uses")} required /></div>
            <div className="space-y-2"><Label>Image URL (optional)</Label><Input data-testid="otc-form-image" value={form.image} onChange={set("image")} /></div>
            <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="otc-form-save">{loading ? "Saving..." : "Save Product"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete {delTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This removes the product from the OTC store. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="otc-confirm-delete" className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
